import { cn } from "@/lib/utils";
import {
  IoValueBody,
  IoValueCard,
} from "@/components/Spotflow/WorkflowRunner/Outputs/IoValueCard";
import {
  IoTruncationNote,
  type IoRowTruncation,
} from "@/components/Spotflow/WorkflowRunner/Outputs/ioTruncationNote";
import { ErrorDetailsView } from "@/components/Spotflow/WorkflowRunner/Outputs/errorDisplay";

export type { IoRowTruncation };

/** Collapsible output row shared by the run-progress drawer and the node
 *  preview panel's Run output modal. `value` is the already-resolved display
 *  value — callers slice fan-out iteration arrays before passing it. */
export function V2OutputRow({
  label,
  value,
  type,
  isError = false,
  truncation,
  nodeId,
}: {
  label: string;
  value: unknown;
  type: string;
  isError?: boolean;
  truncation?: IoRowTruncation;
  /** Owning node — lets a structured error resolve its refs to variable
   *  badges. Optional so non-error rows need not thread it. */
  nodeId?: string;
}) {
  // List-ness comes from the displayed value's shape, not the stored type —
  // paging a fan-out node hands us one item at a time.
  const isListValue = !isError && Array.isArray(value);

  return (
    <IoValueCard
      header={
        <span
          className={cn(
            "flex-1 min-w-0 truncate text-sm font-medium",
            isError ? "text-red-700 dark:text-red-300" : "text-foreground"
          )}
        >
          {isError ? "Error details" : label}
        </span>
      }
      value={value}
      dataType={type}
      isList={isListValue}
      isError={isError}
      defaultOpen
      contentClassName="overflow-x-auto"
      footer={
        truncation && (
          <IoTruncationNote
            truncation={truncation}
            className="border-t border-border/40 px-3 py-1.5"
          />
        )
      }
    >
      {(showRaw) =>
        isError ? (
          <ErrorDetailsView value={value} nodeId={nodeId} />
        ) : (
          <IoValueBody
            value={value}
            dataType={type}
            isList={isListValue}
            showRaw={showRaw}
          />
        )
      }
    </IoValueCard>
  );
}
